import { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { resolveIntlTimeZoneId } from '../utils/resolveIntlTimeZone';
import { formatUtcInTimeZone, type UtcFormatStyle } from '../utils/formatUtcInTimeZone';

export interface TimezoneInfo {
  timeZoneId: string;
  displayName: string;
  currentUtcOffset: string;
  isDaylightSavingTime: boolean;
}

export interface UseTimezoneReturn {
  /** Current timezone information */
  timezoneInfo: TimezoneInfo | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Format a UTC date string to local timezone */
  formatToLocalTime: (utcDateString: string, format?: UtcFormatStyle) => string;
  /** Get the current timezone offset in minutes */
  getTimezoneOffset: () => number;
  /** Check if timezone is loaded */
  isTimezoneLoaded: () => boolean;
}

export const useTimezone = (): UseTimezoneReturn => {
  const [timezoneInfo, setTimezoneInfo] = useState<TimezoneInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadTimezoneInfo = async () => {
      try {
        setLoading(true);
        setError(null);

        const timezone = await adminService.getCurrentTimezone();
        if (!cancelled) {
          setTimezoneInfo(timezone);
        }
      } catch (err) {
        console.warn('useTimezone: Failed to load timezone from server, using browser timezone:', err);
        if (!cancelled) {
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const fallbackTimezone: TimezoneInfo = {
            timeZoneId: browserTimezone,
            displayName: browserTimezone,
            currentUtcOffset: new Date().getTimezoneOffset().toString(),
            isDaylightSavingTime: false
          };
          setTimezoneInfo(fallbackTimezone);
          setError('Using browser timezone as fallback');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTimezoneInfo();

    const onSystemTimezoneChanged = () => {
      loadTimezoneInfo();
    };
    window.addEventListener('systemTimezoneChanged', onSystemTimezoneChanged);

    return () => {
      cancelled = true;
      window.removeEventListener('systemTimezoneChanged', onSystemTimezoneChanged);
    };
  }, []);

  const formatToLocalTime = (utcDateString: string, format: UtcFormatStyle = 'datetime'): string => {
    const timezone = resolveIntlTimeZoneId(timezoneInfo?.timeZoneId);
    const result = formatUtcInTimeZone(utcDateString, timezone, format);
    if (result === 'Invalid date' && utcDateString) {
      console.warn('useTimezone: Invalid date string:', utcDateString);
    }
    return result;
  };

  const getTimezoneOffset = (): number => {
    if (timezoneInfo?.currentUtcOffset) {
      return parseInt(timezoneInfo.currentUtcOffset, 10);
    }
    return new Date().getTimezoneOffset();
  };

  const isTimezoneLoaded = (): boolean => {
    return !loading && timezoneInfo !== null;
  };

  return {
    timezoneInfo,
    loading,
    error,
    formatToLocalTime,
    getTimezoneOffset,
    isTimezoneLoaded
  };
};
